import './Chip.scss'
import type { ComponentProps, ReactNode } from 'react'
import { MdOutlineClose } from 'react-icons/md'
import { clx } from '../clx'

export type ChipTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent'

export interface ChipProps extends Omit<ComponentProps<'span'>, 'children'> {
  label: ReactNode
  tone?: ChipTone
  size?: 'sm' | 'md'
  /** Leading icon (decorative; it gets aria-hidden). */
  icon?: ReactNode
  /** Shows a remove button. */
  onRemove?: () => void
  /** Accessible name of the remove button. Default "Remove <label>" for a text label. */
  removeLabel?: string
}

export function Chip({
  label,
  tone = 'neutral',
  size = 'md',
  icon,
  onRemove,
  removeLabel,
  className,
  ref,
  ...rest
}: ChipProps) {
  const text = typeof label === 'string' || typeof label === 'number' ? String(label) : ''
  return (
    <span
      ref={ref}
      {...rest}
      className={clx(
        'chip',
        `chip--as-${tone}`,
        size !== 'md' && `chip--is-${size}`,
        icon !== undefined && 'chip--has-icon',
        onRemove && 'chip--has-remove',
        className
      )}
    >
      {icon !== undefined && (
        <span className="chip__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="chip__label">{label}</span>
      {onRemove && (
        <button
          type="button"
          className="chip__remove"
          aria-label={removeLabel ?? (text ? `Remove ${text}` : 'Remove')}
          onClick={onRemove}
        >
          <MdOutlineClose aria-hidden="true" />
        </button>
      )}
    </span>
  )
}
